// External dependencies
import PropTypes from 'prop-types';
import React from 'react';

// Try to import recharts, fallback to canvas if not available
let RechartsAvailable = false;
let ComposedChart;
let RadialBarChart;
let RadialBar;
let Bar;
let Line;
let Area;
let XAxis;
let YAxis;
let CartesianGrid;
let Tooltip;
let Legend;
let ResponsiveContainer;
let Cell;

try {
  const recharts = require('recharts');
  ComposedChart = recharts.ComposedChart;
  RadialBarChart = recharts.RadialBarChart;
  RadialBar = recharts.RadialBar;
  Bar = recharts.Bar;
  Line = recharts.Line;
  Area = recharts.Area;
  XAxis = recharts.XAxis;
  YAxis = recharts.YAxis;
  CartesianGrid = recharts.CartesianGrid;
  Tooltip = recharts.Tooltip;
  Legend = recharts.Legend;
  ResponsiveContainer = recharts.ResponsiveContainer;
  Cell = recharts.Cell;
  RechartsAvailable = true;
} catch (e) {
  // Recharts not installed, will use canvas fallback
  RechartsAvailable = false;
}

export default function DashboardChart({
  type,
  data,
  label,
  color = '#2196F3',
}) {
  // Transform data for recharts format
  const chartData = data.map((d, index) => {
    if (typeof d === 'number') {
      return {
        name: `${label} ${index + 1}`,
        value: d,
      };
    }
    return {
      name: d.label || `${label} ${index + 1}`,
      value: d.value || 0,
    };
  });

  const colors = [
    '#4CAF50',
    '#2196F3',
    '#FF9800',
    '#9C27B0',
    '#F44336',
    '#00BCD4',
    '#FFC107',
    '#E91E63',
  ];

  // Use recharts if available
  if (RechartsAvailable) {
    if (type === 'composed') {
      return (
        <div className="admin-dashboard__chart">
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Area
                type="monotone"
                dataKey="value"
                fill={color}
                fillOpacity={0.3}
                stroke={color}
              />
              <Bar dataKey="value" fill={color} />
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={{ fill: color, r: 4 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      );
    }

    if (type === 'radial') {
      return (
        <div className="admin-dashboard__chart">
          <ResponsiveContainer width="100%" height={300}>
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="20%"
              outerRadius="80%"
              data={chartData}
              startAngle={90}
              endAngle={-270}
            >
              <RadialBar
                minAngle={15}
                label={{ position: 'insideStart', fill: '#fff' }}
                background
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={colors[index % colors.length]}
                  />
                ))}
              </RadialBar>
              <Legend
                iconSize={10}
                layout="vertical"
                verticalAlign="middle"
                wrapperStyle={{
                  top: 'center',
                  right: 0,
                  transform: 'translate(0, -50%)',
                }}
              />
              <Tooltip />
            </RadialBarChart>
          </ResponsiveContainer>
        </div>
      );
    }
  }

  // Fallback to canvas rendering if recharts is not available
  return (
    <div className="admin-dashboard__chart">
      <div className="admin-dashboard__chart-fallback">
        <p>Chart library not available. Please install recharts:</p>
        <code>npm install recharts --legacy-peer-deps</code>
        <div className="admin-dashboard__chart-data">
          {chartData.map((item, index) => (
            <div key={index} style={{ margin: '5px 0' }}>
              <strong>{item.name}:</strong> {item.value}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

DashboardChart.propTypes = {
  type: PropTypes.oneOf(['composed', 'radial']).isRequired,
  data: PropTypes.array.isRequired,
  label: PropTypes.string,
  color: PropTypes.string,
};
